/**
 * <DocumentUploadModal> — single-file upload form.
 *
 * Props:
 *   open, onClose, onUploaded
 *   scope: 'property' | 'tenant'
 *   propertyId / tenantId  (one is required, based on scope)
 *   propertyName / tenantName (for header display)
 */
import React, { useState } from 'react';
import axios from 'axios';
import { X, UploadCloud, FileText, Lock } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import {
  CATEGORY_LABELS, PROPERTY_CATEGORIES, TENANT_CATEGORIES,
  SENSITIVE_CATEGORIES, MAX_FILE_SIZE, ALLOWED_MIME,
  fileToBase64, formatBytes,
} from '../../utils/documentsHelpers';
import { track } from '../../services/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DocumentUploadModal = ({
  open, onClose, onUploaded,
  scope, propertyId, tenantId,
  propertyName, tenantName,
}) => {
  const { token } = useAuth();
  const { toast } = useToast();

  const categories = scope === 'property' ? PROPERTY_CATEGORIES : TENANT_CATEGORIES;
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState(categories[0]);
  const [expiryDate, setExpiryDate] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED_MIME.includes(f.type)) {
      toast({ title: 'Unsupported file type', description: 'Use PDF, JPG, PNG, or WebP.', variant: 'destructive' });
      return;
    }
    if (f.size > MAX_FILE_SIZE) {
      toast({ title: 'File too large', description: `Max 5 MB. Yours is ${formatBytes(f.size)}.`, variant: 'destructive' });
      return;
    }
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, '').slice(0, 80));
  };

  const reset = () => {
    setFile(null); setTitle(''); setCategory(categories[0]);
    setExpiryDate(''); setNotes(''); setSubmitting(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!file || !title.trim()) {
      toast({ title: 'Missing fields', description: 'Pick a file and add a title.', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const data_b64 = await fileToBase64(file);
      const body = {
        property_id: scope === 'property' ? propertyId : null,
        tenant_id:   scope === 'tenant'   ? tenantId   : null,
        title: title.trim(),
        category,
        file_name: file.name,
        mime_type: file.type,
        data_b64,
        expiry_date: expiryDate || null,
        notes: notes.trim() || null,
      };
      await axios.post(`${API}/documents`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      track('document_uploaded', { scope, category, size: file.size, has_expiry: !!expiryDate });
      toast({
        title: 'Uploaded',
        description: SENSITIVE_CATEGORIES.has(category)
          ? `${CATEGORY_LABELS[category]} stored & encrypted at rest.`
          : `${CATEGORY_LABELS[category]} saved to vault.`,
      });
      onUploaded?.();
      reset();
      onClose();
    } catch (err) {
      toast({
        title: 'Upload failed',
        description: err.response?.data?.detail || err.message || 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const isSensitive = SENSITIVE_CATEGORIES.has(category);
  const targetLabel = scope === 'property' ? propertyName : tenantName;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="max-w-lg" data-testid="document-upload-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UploadCloud size={18} /> Upload document
          </DialogTitle>
          {targetLabel && (
            <p className="text-sm text-gray-500">
              For <span className="font-semibold text-gray-700">{targetLabel}</span> ({scope})
            </p>
          )}
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label>File *</Label>
            <label className={`mt-1 flex flex-col items-center justify-center w-full rounded-xl border-2 border-dashed cursor-pointer p-6 transition-colors ${file ? 'border-emerald-400 bg-emerald-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'}`}>
              {file ? (
                <div className="text-center">
                  <FileText size={28} className="text-emerald-600 mx-auto mb-1" />
                  <p className="text-sm font-medium text-gray-900 break-all px-3">{file.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{formatBytes(file.size)} · {file.type}</p>
                  <button
                    type="button"
                    onClick={(ev) => { ev.preventDefault(); setFile(null); }}
                    className="mt-2 text-xs text-red-600 hover:underline"
                    data-testid="document-upload-clear-file"
                  >
                    Remove file
                  </button>
                </div>
              ) : (
                <>
                  <UploadCloud size={28} className="text-gray-400 mb-1" />
                  <p className="text-sm font-medium text-gray-700">Click to pick a file</p>
                  <p className="text-xs text-gray-500">PDF, JPG, PNG, WebP · max 5 MB</p>
                </>
              )}
              <input
                type="file"
                onChange={onFileChange}
                accept=".pdf,image/jpeg,image/png,image/webp"
                className="hidden"
                data-testid="document-upload-file-input"
              />
            </label>
          </div>

          <div>
            <Label>Title *</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Megha Aadhaar Front"
              maxLength={200}
              data-testid="document-upload-title"
              required
            />
          </div>

          <div>
            <Label>Category *</Label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="document-upload-category"
            >
              {categories.map(c => (
                <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
              ))}
            </select>
            {isSensitive && (
              <p className="mt-1 text-xs text-emerald-700 flex items-center gap-1">
                <Lock size={11} /> This category will be encrypted at rest.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Expiry date (optional)</Label>
              <Input
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                min={new Date().toISOString().slice(0, 10)}
                data-testid="document-upload-expiry"
              />
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any short note"
                maxLength={300}
                data-testid="document-upload-notes"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => { reset(); onClose(); }}
              className="flex-1"
              data-testid="document-upload-cancel"
            >
              <X size={14} className="mr-1" /> Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700"
              disabled={submitting}
              data-testid="document-upload-submit"
            >
              <UploadCloud size={14} className="mr-1" />
              {submitting ? 'Uploading...' : 'Upload'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentUploadModal;
